import { Component, EventEmitter, Inject, Input, OnInit, Output, PLATFORM_ID, ViewEncapsulation } from '@angular/core';
import { TourInfoTranslation } from '../../modules/tour-info/tour-info.translation';
import { Mote, MoteEventType, Voucher } from '../../models';
import { AutoSub, AutoSubOnDestroy, MoteEventService, UserStateService, VoucherService } from '../../providers';
import { isPlatformServer } from '@angular/common';

type TourState = 'unset' | 'continue' | 'start' | 'purchase' | 'unavailable' | 'loading';

@AutoSubOnDestroy()
@Component({
    selector: 'start-tour-button',
    templateUrl: './start-tour-button.component.html',
    styleUrls: ['./start-tour-button.component.scss'],
    encapsulation: ViewEncapsulation.None
}) 
export class StartTourButtonComponent implements OnInit {
    
    public tourState: TourState = 'unset';
    public tourStateLabels: { [ tourState in TourState ]?: string } = {};
 
    @Output() startTourClicked = new EventEmitter<void>();
    @Output() buyTourClicked = new EventEmitter<void>();

    /** While true, loading will show until intro-flow has closed */
    @Input() startTourLoadEnabled: boolean = true;
    @Input('info') set setInfo(info: { tour: Mote, voucher?: Voucher }) {
        this.tour = info.tour;
        this.voucher = info.voucher;
        this.updateStateLabels();
        this.updateTourState();
    }

    public tour: Mote;
    private voucher: Voucher;

    constructor(
        private userStateService: UserStateService,
        private voucherService: VoucherService,
        private moteEventService: MoteEventService,
        @Inject(PLATFORM_ID) private platform: any,
    ) {
    }

    ngOnInit(): void {
        // Stop loading indicator on intro-flow close
        AutoSub(this).reg['tourStartSub'] = this.moteEventService.watchEventType(MoteEventType.INTROFLOW_CLOSED).subscribe(() => {
            this.updateTourState();
        })
    }

    private updateStateLabels() {
        if(!this.tour) return;

        const tourTranslation = new TourInfoTranslation(this.tour.language, this.tour.duration, this.tour.distance, this.tour.startPlace, this.tour.endPlace, this.tour.storyCount, this.tour.city);
        this.tourStateLabels = {
            'continue': tourTranslation.getText('continueTour'),
            'start': tourTranslation.getText('startTour'),
            'purchase': tourTranslation.getText('buyTour'),
            'unavailable': tourTranslation.getText('unavailableTour'),
            'loading': tourTranslation.getText('loading')
        }
    }

    private async updateTourState() {
        if(!this.tour) {
            this.tourState = this.voucher ? 'unavailable' : 'unset';
            return;
        }

        const activeAudio = isPlatformServer(this.platform) ? null : await this.userStateService.getStoredActiveAudio();
        this.tourState = 
            (activeAudio?.tourKey == this.tour.key) ? 'continue' :
            (!this.tour.productLocked) ? 'start' :
            (!this.voucher) ? 'purchase' :
            (this.voucher.tourStartedOn) ? 'continue' : 'start';
    }

    public buttonClick() {
        switch(this.tourState) {
            case 'continue':
            case 'start':
                if(this.startTourLoadEnabled) this.tourState = 'loading';
                if(this.voucher) this.voucherService.setTourStartedOn(this.voucher);
                this.moteEventService.emitMoteEvent({ mote: this.tour, type: MoteEventType.START_TOUR });
                this.startTourClicked.emit();
                break;
            case 'purchase':
                this.buyTourClicked.emit();
                break;
            default: break;
        }
    }
}
